import React, { useState } from "react";
import { useModal } from "react-modal-hook";
import {
  SubmissionType,
  useDeleteSubmissionTypeMutation,
} from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { spacing } from "../../../../stitches.config";
import { FlexColumn } from "../../../Common/__styles__/Layout";
import { CommonModal } from "../../../Common/CommonModal";
import Modal from "../../../Common/Modal";
import { Body } from "../../../Common/Typography";
import Checkbox from "../../../Inputs/Checkbox";

const DeleteSubmissionTypeModal = ({
  submissionType,
  hideModal,
  onDeleteSuccess,
}: {
  submissionType: Pick<SubmissionType, "id" | "name">;
  hideModal: () => void;
  onDeleteSuccess: () => void;
}) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const [confirmHighlightsRemoval, setConfirmHighlightsRemoval] =
    useState(false);

  const [deleteSubmissionType, { loading }] = useDeleteSubmissionTypeMutation({
    onCompleted: () => {
      hideModal();
      addSuccessToast("Your record type was successfully deleted");
      onDeleteSuccess();
    },
    onError: () => {
      addErrorToast(
        "There was a problem deleting the record type. Please try again or contact support@withforerunner.com",
      );
    },
  });

  return (
    <CommonModal
      title="Delete record type"
      subtitle={submissionType.name}
      primaryButtonText="Delete"
      variant="destructive"
      dataTestId="delete-submission-type-modal"
      disabled={!confirmHighlightsRemoval}
      onSubmit={() => {
        void deleteSubmissionType({
          variables: { submissionTypeId: submissionType.id },
        });
      }}
      loading={loading}
      onCancel={hideModal}
      size="medium"
      content={
        <FlexColumn style={{ gap: spacing.m.value }}>
          <Body size="default" type="regular">
            By deleting this record type, you confirm that:
          </Body>
          <FlexColumn style={{ gap: spacing.s.value }}>
            <Checkbox
              value={confirmHighlightsRemoval}
              onChange={setConfirmHighlightsRemoval}
              label="Any highlights on your public website homepage that are linked to this record type will be removed."
              required
            />
          </FlexColumn>
          <Body size="default" type="regular">
            This record type can only be deleted because there are no existing
            records associated with it. This action{" "}
            <span style={{ fontWeight: 500 }}>cannot</span> be undone.
          </Body>
        </FlexColumn>
      }
    />
  );
};

export const useDeleteSubmissionTypeModal = ({
  onDeleteSuccess,
}: {
  onDeleteSuccess: () => void;
}) => {
  const [submissionType, setSubmissionType] = useState<Pick<
    SubmissionType,
    "id" | "name"
  > | null>(null);

  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <DeleteSubmissionTypeModal
          submissionType={submissionType!}
          hideModal={() => {
            hideModal();
            setSubmissionType(null);
          }}
          onDeleteSuccess={onDeleteSuccess}
        />
      </Modal>
    ),
    [submissionType, onDeleteSuccess],
  );

  const showDeleteSubmissionTypeModal = (
    submissionType: Pick<SubmissionType, "id" | "name">,
  ) => {
    setSubmissionType(submissionType);
    showModal();
  };

  return showDeleteSubmissionTypeModal;
};
